import React, { useEffect } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { FaCube, FaPencilRuler, FaCheckCircle } from 'react-icons/fa';
import ProductDevContactForm from '../components/ProductDevContactForm';
import './ProductDevelopmentPage.css';

const ProductDevelopmentPage = () => {
  // Animation on scroll effect
  useEffect(() => {
    const handleScrollAnimation = () => {
      const scrollElements = document.querySelectorAll('.animate-on-scroll');

      scrollElements.forEach(element => {
        const elementPosition = element.getBoundingClientRect().top;
        const windowHeight = window.innerHeight;

        if (elementPosition < windowHeight - 100) {
          element.classList.add('animate');
        }
      });
    };

    window.addEventListener('scroll', handleScrollAnimation);
    handleScrollAnimation(); // Initial check

    return () => window.removeEventListener('scroll', handleScrollAnimation);
  }, []);

  const heroStyle = {
    backgroundImage: `
      linear-gradient(135deg, rgba(14, 162, 189, 0.75) 0%, rgba(11, 125, 142, 0.75) 100%),
      url(${process.env.PUBLIC_URL}/images/product-development-bg-v2.png)
    `,
    backgroundSize: 'cover',
    backgroundPosition: 'center center',
    backgroundRepeat: 'no-repeat'
  };

  return (
    <div className="product-dev-page">
      {/* Hero Section */}
      <section className="product-dev-hero" style={heroStyle}>
        <div className="hero-overlay"></div>
        <Container>
          <Row className="align-items-center">
            <Col lg={8} className="mx-auto text-center">
              <h1 className="hero-title animate-on-scroll">
                3D Printing & <span className="highlight">Prototyping Service</span>
              </h1>
              <p className="hero-subtitle animate-on-scroll">
                FDM 3D printing service based in Ireland. Submit your CAD files or request modeling assistance for hardware prototypes.
              </p>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Services Overview */}
      <section className="services-overview">
        <Container>
          <Row>
            <Col lg={6} className="mb-4">
              <div className="service-box animate-on-scroll">
                <div className="service-icon-large">
                  <FaCube />
                </div>
                <h3>3D Printing Service</h3>
                <p>
                  Have a CAD file ready? Upload it and we'll print using FDM technology.
                  Best suited for functional prototypes and mechanical parts.
                </p>
                <ul className="service-features">
                  <li><FaCheckCircle /> PLA, PETG materials available</li>
                  <li><FaCheckCircle /> 256 x 256 x 256 mm build volume</li>
                  <li><FaCheckCircle /> Typical turnaround: 3-7 days</li>
                  <li><FaCheckCircle /> Shipping within Ireland/EU</li>
                </ul>
                <div className="supported-formats">
                  <strong>Supported formats:</strong> .stl, .obj, .step, .stp, .iges, .igs, .3mf, .amf, .brep, .zip
                </div>
              </div>
            </Col>

            <Col lg={6} className="mb-4">
              <div className="service-box animate-on-scroll">
                <div className="service-icon-large">
                  <FaPencilRuler />
                </div>
                <h3>CAD Modeling Service</h3>
                <p>
                  Need a CAD file created? We can model your design using FreeCAD.
                  Best for simple mechanical parts and enclosures.
                </p>
                <ul className="service-features">
                  <li><FaCheckCircle /> Basic CAD modeling in FreeCAD</li>
                  <li><FaCheckCircle /> Design review for printability</li>
                  <li><FaCheckCircle /> Printing available after approval</li>
                  <li><FaCheckCircle /> Quote provided after review</li>
                </ul>
                <div className="supported-formats">
                  <strong>Best for:</strong> Simple enclosures, brackets, and functional parts
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Process Section */}
      <section className="process-section">
        <Container>
          <div className="text-center mb-5 animate-on-scroll">
            <h2>How It Works</h2>
            <p className="lead">Submit request, receive quote, approve, and receive your printed part</p>
          </div>

          <Row>
            <Col md={3} className="mb-4">
              <div className="process-step animate-on-scroll">
                <div className="step-number">1</div>
                <h4>Submit Request</h4>
                <p>Upload your CAD file via the form below, or describe what you need modeled</p>
              </div>
            </Col>

            <Col md={3} className="mb-4">
              <div className="process-step animate-on-scroll" style={{animationDelay: '0.1s'}}>
                <div className="step-number">2</div>
                <h4>Review & Quote</h4>
                <p>We review your file/request and send a quote (usually within 24-48 hours)</p>
              </div>
            </Col>

            <Col md={3} className="mb-4">
              <div className="process-step animate-on-scroll" style={{animationDelay: '0.2s'}}>
                <div className="step-number">3</div>
                <h4>Production</h4>
                <p>After approval, we model (if needed) and print your part</p>
              </div>
            </Col>

            <Col md={3} className="mb-4">
              <div className="process-step animate-on-scroll" style={{animationDelay: '0.3s'}}>
                <div className="step-number">4</div>
                <h4>Delivery</h4>
                <p>Finished part ships via An Post or courier (Ireland/EU addresses)</p>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Contact Form Section */}
      <section className="contact-form-section">
        <Container>
          <div className="text-center mb-5 animate-on-scroll">
            <h2>Request a Quote</h2>
            <p className="lead">
              Upload your CAD file or describe your modeling needs
            </p>
          </div>

          <Row>
            <Col lg={8} className="mx-auto">
              <div className="form-wrapper animate-on-scroll">
                <ProductDevContactForm />
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </div>
  );
};

export default ProductDevelopmentPage;
